// The shape of GET /api/lab/health (apps/api/src/lab/types.ts). Each part stands alone.

export type Part<T> = ({ available: true } & T) | { available: false; reason: string };

export interface Series {
  latest: number | null;
  avg: number | null;
  max: number | null;
  points: { t: string; v: number }[];
}

export interface Container {
  name: string;
  service: string;
  image: string;
  state: string;
  health: string | null;
  exitCode: number;
  restarts: number;
  startedAt: string | null;
  status: string;
  problem: string | null;
}

export interface Certificate {
  host: string;
  file: string;
  issuer: string | null;
  notAfter: string | null;
  daysLeft: number | null;
  trusted: boolean;
  problem: string | null;
}

export interface Finding {
  severity: "critical" | "warning" | "info";
  title: string;
  detail: string;
}

export interface Database {
  id: string;
  status: string;
  class: string | null;
  engine: string | null;
  engineVersion: string | null;
  allocatedGiB: number | null;
  multiAz: boolean;
  backupRetentionDays: number | null;
  latestRestorableTime: string | null;
  metrics: Part<{ cpu: Series; freeStorageBytes: Series; connections: Series; freeableMemoryBytes: Series }>;
}

export interface Host {
  instance: Part<{ id: string; type: string | null; state: string; launchedAt: string | null; publicIp: string | null }>;
  cpu: Part<Series>;
  memory: Part<{ totalBytes: number; availableBytes: number }>;
  load: Part<{ one: number; five: number; fifteen: number; cpus: number | null; uptimeSeconds: number | null }>;
  disk: Part<{ path: string; sizeBytes: number; usedBytes: number; freeBytes: number }>;
  dockerDisk: Part<{ rows: { type: string; total: string; active: string; size: string; reclaimable: string }[] }>;
}

export interface LabHealth {
  checkedAt: string;
  status: "ok" | "warning" | "critical";
  findings: Finding[];
  host: Host;
  database: Part<Database>;
  containers: Part<{ projects: { project: string; containers: Container[] }[]; total: number; expected: number; healthy: number }>;
  certificates: Part<{ target: string; sites: Certificate[] }>;
  backups: {
    database: Part<{ retentionDays: number | null; latestRestorableTime: string | null }>;
    hostDisk: { policy: string | null; note: string };
  };
}

const GiB = 1024 ** 3;
export const gib = (bytes: number) => `${(bytes / GiB).toFixed(bytes < 10 * GiB ? 1 : 0)} GiB`;
export const pct = (n: number | null | undefined) => (n === null || n === undefined ? "—" : `${Math.round(n)}%`);

export function since(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

export function duration(seconds: number): string {
  const d = Math.floor(seconds / 86_400);
  const h = Math.floor((seconds % 86_400) / 3600);
  if (d) return `${d} d ${h} h`;
  const m = Math.floor((seconds % 3600) / 60);
  return h ? `${h} h ${m} min` : `${m} min`;
}
